import proj4 from 'proj4';
import { register as OlRegister } from 'ol/proj/proj4';
import OlMap from 'ol/Map';
import OlView from 'ol/View';
import { DragRotateAndZoom, defaults as OlDefaultInteractions } from 'ol/interaction';
import OlStyle from 'ol/style/Style';
import OlStroke from 'ol/style/Stroke';
import OlFeature from 'ol/Feature';
import OlPolygon from 'ol/geom/Polygon';
import OlLinearRing from 'ol/geom/LinearRing';
import OlFilterMask from 'ol-ext/filter/Mask';
import OlFill from 'ol/style/Fill';
import { getPointResolution } from 'ol/proj';

import GeoJSON from 'ol/format/GeoJSON';
import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import OLCesium from 'olcs';
import {
    PROJECTION_CODE, PROJ4_PROJECTION_DEFENITION, NEDGLOBE_MAP_PROJECTION,
    DEFAULT_FEATURE_STYLE_SETTINGS, NEDGLOBE_VIEWER, NG_LAYER_TYPES,
    DEFAULT_DOTS_PER_INCH, MMPERINCH, INCHES_PER_METER, NEDGLOBE_MAP_RESOLUTION,
    OWS_TYPE
} from '../../constants';

import { getBoundingBoxWithMargin } from './boundingBox';
import { createLayer } from './layer';
import { getStyleSettings, getStyle } from './layer/styles';

/**
 * Register NedGlobe openlayers map
 * @param {String} target id of the element to load the map in
 */
export function registerNedGlobeMap(target, user, interactions, controls) {
    registerProjection();
    let bbox = getBoundingBoxWithMargin(user);

    const view = initViewObject(NEDGLOBE_MAP_PROJECTION, bbox);
    interactions = interactions ?? OlDefaultInteractions({
        altShiftDragRotate: true,
        pinchRotate: false,
        doubleClickZoom: false
    }).extend([new DragRotateAndZoom()]);
    return initMapObject(target, view, interactions, controls);
}

/**
 * Unregister NedGlobe openlayers map
 * @param {String} target id of the element to unload the map from
 */
export function unRegisterNedGlobeMap(map) {
    map.setTarget(null);
}

/**
 * Register default projection in proj4
 */
export function registerProjection(code = PROJECTION_CODE, projDefinition = PROJ4_PROJECTION_DEFENITION) {
    proj4.defs(code, projDefinition);
    OlRegister(proj4);
}

/**
 * get a openlayers view for a map
 * @param {ol/proj/Projection} projection openlayers Projection
 * @param {Array} extent openlayers extent object
 * @param {Number} [minResolution = 0.02625] closest zoom
 * @param {Number} [maxResolution = 430.08] farthest zoom
 * @returns {ol/View} openlayers view object
 */
export function initViewObject(projection, extent) {
    return new OlView({
        center: [0, 0],
        projection: projection,
        resolutions: NEDGLOBE_MAP_RESOLUTION,
        constrainResolution: false,
        zoom: 0,
        rotation: 0,
        showFullExtent: true,
        extent
    });
}

/**
 * initialize a openlayers map object
 * @param {String} target id of the element to load the map in
 * @param {ol/View} view openlayers Projection
 * @returns {ol/Map} openlayers view object
 */
export function initMapObject(target, view, interactions, controls) {
    return new OlMap({
        target: target,
        logo: false,
        layers: [],
        loadTilesWhileInteracting: false,
        controls,
        pixelRatio: 1,
        interactions,
        view: view
    });
}

export function init3DMapObject(map) {
    const ol3DMap = new OLCesium({
        map: map,
        target: 'nedglobe.map3d',
        time: function () {
            return window.Cesium.JulianDate.now();
        }
    });
    return ol3DMap;
}

/**
     * Add a layer to the map
     * @param {object} store vuex store reference
     * @param {ol/Map} map Map to load the layer in
     * @param {object} layerObject The object for the layer in the store
     * @returns {Promise} promise object resolving the layer object
     */
export async function prepareLayer(store, map, layerObject, highQuality = false, alwaysCreateWfsLayer = false) {
    if (layerObject.extended.serviceType !== OWS_TYPE.WFS && layerObject.extended.serviceType !== OWS_TYPE.WMS &&
        layerObject.type === NG_LAYER_TYPES.CUSTOM) {
        const computedStyles = layerObject.extended.computedStyles || await getStyleSettings([DEFAULT_FEATURE_STYLE_SETTINGS]);
        let features = layerObject.data.data;
        if (layerObject.extended.serviceType === 'geoJSON') {
            features = new GeoJSON().readFeatures(features);
        }
        const vectorSource = new VectorSource({ features });
        const styleFunction = (feature, resolution) => {
            return getStyle(
                computedStyles,
                feature,
                resolution,
                NEDGLOBE_VIEWER.styleDisplayTypes.default,
                layerObject.extended.feedbackFields
            );
        };
        return new VectorLayer({
            source: vectorSource,
            style: styleFunction,
            minResolution: layerObject.extended?.minResolution,
            maxResolution: layerObject.extended?.maxResolution
        });
    }

    const view = map.getView();
    const size = map.getSize();
    let extent;
    try {
        extent = view.calculateExtent(size);
    } catch (err) {
        extent = null;
    }
    const extendedLayerData = layerObject.extended;
    const filters = layerObject?.filters?.settings?.map(({ filter }) => filter);
    if (extendedLayerData.serviceType) {
        return createLayer(store, extendedLayerData, extent, filters, highQuality, alwaysCreateWfsLayer);
    } else {
        return layerObject.extended;
    }
}

/**
    * Adaptation of OL6 CenterOn, to center on a position while zooming to another resolution than the current one
    * This does not call view.setCenter on the determined coordinates, but returns them in stead
    * Rotation is copied from the original function, and should stay since GPS tracking may rotate the map
    * @param {Array.<number>} coordinate - Coordinate to be placed at the given position
    * @param {Array.<number>} size - Pixel map size
    * @param {Object} position - Pixel Position on the view to center on.
    * @param {OlView} view - the map view
    * @param {Number} newResolution - the desired resolution after zoom change
    * @returns {Array.<number>} returns the coordinate array for position for OpenLayers to center on
    */
export function getAdjustedCenterOn(coordinate, size, position, view, newResolution) {
    // calculate rotated position
    const rotation = view.getRotation();
    const cosAngle = Math.cos(-rotation);
    let sinAngle = Math.sin(-rotation);
    let rotX = coordinate[0] * cosAngle - coordinate[1] * sinAngle;
    let rotY = coordinate[1] * cosAngle + coordinate[0] * sinAngle;

    rotX += (size[0] / 2 - position[0]) * newResolution;
    rotY += (position[1] - size[1] / 2) * newResolution;

    // go back to original angle
    sinAngle = -sinAngle; // go back to original rotation
    const centerX = rotX * cosAngle - rotY * sinAngle;
    const centerY = rotY * cosAngle + rotX * sinAngle;

    return [centerX, centerY];
}

// OpenLayers documentation:
// minResolution: The minimum resolution (inclusive) at which this layer will be visible.
// maxResolution: The maximum resolution (exclusive) below which this layer will be visible.
export function isInRange(currentResolution, minResolution, maxResolution) {
    if (minResolution && maxResolution) {
        return minResolution <= currentResolution && maxResolution > currentResolution;
    }
    if (minResolution) {
        return minResolution <= currentResolution;
    }
    if (maxResolution) {
        return maxResolution > currentResolution;
    }
    return true;
}

function polygonOutlineStyle(color = 'rgba(0,0,0,1)', width = 3) {
    return new OlStyle({
        stroke: new OlStroke({
            color,
            width
        }),
        zIndex: 99999
    });
}

export function featureMask(feature, color = [0, 0, 0, 0.4]) {
    return new OlFilterMask({
        feature,
        inner: true,
        fill: new OlFill({ color })
    });
}

export function polygonOutline(layer, coords, color, width) {
    if (coords.length > 0) {
        const newPolygonWithDonut = new OlPolygon([[[-1000000, -1000000], [-1000000, 1000000], [1000000, 1000000], [1000000, -1000000], [-1000000, -1000000]]]);
        newPolygonWithDonut.appendLinearRing(new OlLinearRing(coords));
        const feature = new OlFeature({ geometry: newPolygonWithDonut });
        feature.setStyle(polygonOutlineStyle(color, width));

        return feature;
    }
    return null;
}

export function getResolutionFromScale(map, scale, centerCoord) {
    const view = map.getView();
    const projection = view.getProjection();
    const coords = centerCoord || view.getCenter();

    return scale / 1000 / getPointResolution(projection, DEFAULT_DOTS_PER_INCH / MMPERINCH, coords);
}

export function getResolutionFromScaleDpi(scale) {
    return scale / (DEFAULT_DOTS_PER_INCH * INCHES_PER_METER);
}

export function getScaleFromResolution(map, resolution, centerCoord) {
    const view = map.getView();
    const projection = view.getProjection();
    const coords = centerCoord || view.getCenter();

    return resolution * getPointResolution(projection, DEFAULT_DOTS_PER_INCH / MMPERINCH, coords) * 1000;
}

export function getMapFeatureAtPixel(pixel, map, activeLayer) {
    return map.forEachFeatureAtPixel(
        pixel,
        (feature) => {
            return feature;
        },
        {
            // Only get data for the activeLayer
            // Do not get tooltip data for custom layers (selection layers for example)
            layerFilter: (layer) => {
                const id = activeLayer.type === NG_LAYER_TYPES.FEEDBACK ? activeLayer.data.id : activeLayer.data.customId;
                return layer.get('combinedId') === id &&
                    !Object.values(
                        NEDGLOBE_VIEWER.customLayers.printable
                    ).includes(layer.get('title'));
            }
        }
    );
}
